/**
 * A garment's code — `BJB-36-01`: the design's short code, the size (any attribute marked "goes into
 * garment codes"), and a two-digit number per design and size. The server issues it
 * (`GarmentCodes.cs`); this module is the clients' half: reading a code typed the short way, and
 * suggesting a design's short code from its name.
 *
 * <p>
 * <strong>`readGarmentCode` mirrors `GarmentCodes.Read` case for case</strong> — the server reads
 * a typed code for the stock search, this for the rail and the scan box, and the two must find the
 * same garment. `garmentCode.test.ts` and `GarmentCodesTests.cs` carry the same cases.
 * </p>
 */

/** The code a typed text could mean exactly, and the prefix it could start. */
export interface GarmentCodeReading {
  exact: string | null;
  prefix: string | null;
}

const nothing: GarmentCodeReading = { exact: null, prefix: null };

/** The number as printed: two digits, more once the count passes 99. */
export function garmentNumber(value: number): string {
  return String(value).padStart(2, '0');
}

/**
 * `bjb36-1`, `BJB 36 1` and `bjb-36-01` all mean `BJB-36-01`; `bjb 36` means every `BJB-36-`.
 * Letters running into digits are split only in the first part (`BJB36`), never digits into
 * letters (`38R` is a size). One part on its own is left to the ordinary search: `BJB361` could be
 * size 36 number 1 or size 3 number 61, and guessing would show the wrong garment.
 */
export function readGarmentCode(typed: string): GarmentCodeReading {
  const parts = partsOf(typed);

  if (parts.length < 2) {
    return nothing;
  }

  const last = parts[parts.length - 1]!;
  const exact = /^\d+$/.test(last)
    ? `${parts.slice(0, -1).join('-')}-${garmentNumber(Number.parseInt(last, 10))}`
    : null;

  return { exact, prefix: `${parts.join('-')}-` };
}

/** Whether a garment's code answers what was typed — as written, or read the short way. */
export function matchesGarmentCode(serialCode: string, typed: string): boolean {
  const needle = typed.trim().toUpperCase();

  if (!needle) {
    return false;
  }

  const code = serialCode.toUpperCase();

  if (code.includes(needle)) {
    return true;
  }

  const { exact, prefix } = readGarmentCode(needle);

  return code === exact || (prefix !== null && code.startsWith(prefix));
}

function partsOf(typed: string): string[] {
  const parts: string[] = [];

  for (const token of typed
    .trim()
    .toUpperCase()
    .split(/[\s-]+/)) {
    if (!token) {
      continue;
    }

    const boundary = parts.length === 0 ? token.search(/[A-Z]\d/) : -1;

    if (boundary >= 0) {
      parts.push(token.slice(0, boundary + 1), token.slice(boundary + 1));
    } else {
      parts.push(token);
    }
  }

  return parts;
}

/* ----------------------------------------------------------- the short code */

/** Letters only, two to five: it can never run into the size, and nobody mistakes O for 0. */
export const shortCodePattern = /^[A-Z]{2,5}$/;

const fillerWords = new Set(['AND', 'WITH', 'THE', 'OF', 'FOR', 'IN', 'ON', 'A', 'AN']);

/**
 * A design's short code from its name: the first letter of each word, skipping "and", "with" and
 * the like — Gold Zari Sherwani is GZS, Black Jodhpuri Bandhgala BJB. A one-word name takes its
 * first letter and the next two consonants (Sherwani is SHR). When the suggestion is taken, letters
 * from the last word are added until it is free (GZS, GZSH, GZSHE), and then the last letter is
 * stepped through the alphabet. Null when the name has no letters or nothing free fits in five.
 *
 * <p>
 * Only a suggestion: the person can type any code, and the server is the guard against a duplicate.
 * </p>
 */
export function suggestShortCode(
  name: string,
  taken: ReadonlySet<string> = new Set(),
): string | null {
  const words = name
    .toUpperCase()
    .split(/[^A-Z]+/)
    .filter((word) => word.length > 0 && !fillerWords.has(word));

  if (words.length === 0) {
    return null;
  }

  const base =
    words.length === 1
      ? oneWord(words[0]!)
      : words
          .slice(0, 5)
          .map((word) => word[0])
          .join('');

  const free = (code: string) => code.length >= 2 && !taken.has(code);

  if (free(base)) {
    return base;
  }

  // GZS taken: GZSH, then GZSHE — the last word's own letters, so the code still reads as the name.
  const last = words[words.length - 1]!;
  let longer = base;

  for (const letter of last.slice(1)) {
    if (longer.length >= 5) {
      break;
    }

    longer += letter;

    if (free(longer)) {
      return longer;
    }
  }

  // Still taken at five: step the last letter through the alphabet.
  const stem = longer.slice(0, Math.min(longer.length, 5) - 1);

  for (let code = 65; code <= 90; code++) {
    const candidate = stem + String.fromCharCode(code);

    if (free(candidate)) {
      return candidate;
    }
  }

  return null;
}

function oneWord(word: string): string {
  const consonants = word
    .slice(1)
    .split('')
    .filter((letter) => !'AEIOU'.includes(letter));

  const code = word[0]! + consonants.slice(0, 2).join('');

  return code.length >= 2 ? code : word.slice(0, 3);
}

/** Why a typed short code would make a poor garment code, or null when it is a good one. */
export function shortCodeProblem(code: string): string | null {
  const value = code.trim().toUpperCase();

  if (!value) {
    return 'A short code is needed — it starts every garment code.';
  }

  if (value.includes('-')) {
    return 'No hyphens: a hyphen separates the parts of a garment code.';
  }

  return shortCodePattern.test(value)
    ? null
    : 'Two to five letters work best, such as BJB — digits run into the size.';
}
